<script setup lang="ts">
    import { Button } from '@/components/ui/button';
    import { computed, watch, ref } from 'vue';
    import { CreditCard, Pencil } from 'lucide-vue-next';
    import { router, usePage } from '@inertiajs/vue3';
    import { SectionHeader } from '@/components/ui/form';
    import { SharedData } from '@/types';
    import { useCompanyClientData } from '@/composables/useCompanyClientData';
    import { useCustomerEmployeeData } from '@/composables/useCustomerEmployeeData';
    import { useI18n } from 'vue-i18n';
    import { useVoucherAssignmentData } from '@/composables/useVoucherAssignmentData';
    import { dedupeAssigneeIds, splitAssigneeIds, toAssigneeIds } from '@/utils/voucherAssignees';
    import VoucherAdministrateCardsForm from './VoucherAdministrateCardsForm.vue';
    import VoucherAdministrateFallbackForm from './VoucherAdministrateFallbackForm.vue';

    const page = usePage<SharedData>();
    const selectedCompanyId = computed(() => page.props.auth.user.selected_company_id);

    const { voucherAssignmentData } = useVoucherAssignmentData(selectedCompanyId.value);
    const { customerEmployees } = useCustomerEmployeeData(selectedCompanyId.value);
    const { clientOptions, isPendingClientOptions } = useCompanyClientData(selectedCompanyId.value);

    const { t } = useI18n();

    const props = defineProps<{
        form: any;
    }>();

    const fallbackForm = ref<any>(null);

    watch([voucherAssignmentData, customerEmployees], ([newVal]) => {
        if (!newVal) {
            return;
        }

        const employees = customerEmployees.value ?? [];

        props.form.cards = JSON.parse(JSON.stringify(newVal.credit_cards));

        const fallbackAssigneeIds = dedupeAssigneeIds(
            toAssigneeIds(
                newVal.voucher_assigned_fallback ?? [],
                newVal.voucher_assigned_fallback_users ?? [],
                employees,
            ),
            employees,
        );
        const fallbackAssignees = splitAssigneeIds(fallbackAssigneeIds);
        props.form.fallback_assignee_ids = fallbackAssigneeIds;
        props.form.fallback_employee_ids = fallbackAssignees.employee_ids;
        props.form.fallback_user_ids = fallbackAssignees.user_ids;

        props.form.voucher_assignment_rules = JSON.parse(
            JSON.stringify(newVal.voucher_assignment_rules),
        ).map((rule: { employee_ids?: number[]; user_ids?: number[] }) => {
            const assigneeIds = dedupeAssigneeIds(
                toAssigneeIds(rule.employee_ids ?? [], rule.user_ids ?? [], employees),
                employees,
            );
            const assignees = splitAssigneeIds(assigneeIds);

            return {
                ...rule,
                employee_ids: assignees.employee_ids,
                user_ids: assignees.user_ids,
                assignee_ids: assigneeIds,
            };
        });

        if (props.form.voucher_assignment_rules.length === 0) {
            fallbackForm.value?.addVoucherAssignmentRule();
        }
    });
</script>

<template>
    <SectionHeader class="flex-col justify-between sm:flex-row">
        <span>{{ t('vouchers.administrate.subtitle') }}</span>
        <Button
            variant="ghost_warning"
            size="none"
            class="text-xs"
            tabindex="-1"
            @click.prevent="router.visit(route('v2.credit-cards.index'))"
        >
            {{ t('vouchers.administrate.administrate_cards') }}
            <Pencil class="text-warning size-3" />
        </Button>
    </SectionHeader>

    <VoucherAdministrateCardsForm
        v-if="form.cards.length > 0"
        :form="form"
        :employee-options="customerEmployees"
        :user-options="clientOptions"
        :is-loading-user-options="isPendingClientOptions"
    />
    <div
        v-else
        class="text-muted-foreground flex flex-col items-center gap-3 pt-5 text-center text-xs"
    >
        <CreditCard />
        <p>
            {{ t('vouchers.administrate.no_cards') }}
        </p>
    </div>

    <SectionHeader class="flex-col justify-between sm:flex-row">
        {{ t('vouchers.administrate.general') }}
    </SectionHeader>

    <VoucherAdministrateFallbackForm
        ref="fallbackForm"
        :form="form"
    />
</template>
